import { ReactNode, useState } from 'react';

// Peças compartilhadas pelas telas de Fotos e Vídeos E-commerce.

// Ícone "estrelinha IA" (sparkles) — SVG inline (o projeto não usa lib de ícones).
export function SparkleIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z" />
      <path d="M20 3v4" /><path d="M22 5h-4" /><path d="M4 17v2" /><path d="M5 18H3" />
    </svg>
  );
}

// O formato de saída ("Responda APENAS com JSON...") é anexado pelo backend.
// O editor esconde essa linha para o usuário não quebrar a etapa sem querer.
export function stripOutputContract(instruction: string) {
  return String(instruction || '').replace(/\s*Responda APENAS com JSON[\s\S]*$/i, '').trim();
}

const AI_REFERENCE_MAX_DIMENSION = 1_600;
const AI_REFERENCE_TARGET_BYTES = 600 * 1_024;

function canvasToBlob(canvas: HTMLCanvasElement, quality: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error('Não foi possível compactar a imagem.')),
      'image/jpeg',
      quality,
    );
  });
}

export async function compressImageForAi(file: File) {
  if (!file.type.startsWith('image/')) {
    throw new Error('Selecione um arquivo de imagem válido.');
  }
  if (file.size <= AI_REFERENCE_TARGET_BYTES) return file;

  const bitmap = await createImageBitmap(file);
  try {
    const initialScale = Math.min(1, AI_REFERENCE_MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
    let width = Math.max(1, Math.round(bitmap.width * initialScale));
    let height = Math.max(1, Math.round(bitmap.height * initialScale));
    let bestBlob: Blob | null = null;

    for (let resizeAttempt = 0; resizeAttempt < 4; resizeAttempt += 1) {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('O navegador não conseguiu processar a imagem.');

      // JPEG não possui transparência. O fundo branco evita áreas transparentes pretas.
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, width, height);
      context.drawImage(bitmap, 0, 0, width, height);

      for (const quality of [0.86, 0.78, 0.7, 0.62, 0.54]) {
        const blob = await canvasToBlob(canvas, quality);
        bestBlob = blob;
        if (blob.size <= AI_REFERENCE_TARGET_BYTES) {
          const baseName = file.name.replace(/\.[^.]+$/, '') || 'referencia';
          return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified });
        }
      }

      const reduction = Math.min(0.85, Math.sqrt(AI_REFERENCE_TARGET_BYTES / bestBlob!.size) * 0.92);
      width = Math.max(1, Math.round(width * reduction));
      height = Math.max(1, Math.round(height * reduction));
    }

    if (!bestBlob) throw new Error('Não foi possível compactar a imagem.');
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'referencia';
    return new File([bestBlob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified });
  } finally {
    bitmap.close();
  }
}

// Indicador do passo a passo (1 Produto → 2 Estilo → 3 Revisar). Passos já
// alcançados ficam clicáveis para voltar e ajustar.
export function WizardHeader({ steps, current, reached, onGo }: {
  steps: string[]; current: number; reached: number; onGo: (step: number) => void;
}) {
  return (
    <ol className="flex items-center gap-2 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
      {steps.map((label, i) => {
        const n = i + 1;
        const active = n === current;
        const done = n < current || (n <= reached && n !== current);
        const clickable = n <= reached && !active;
        return (
          <li key={label} className="flex min-w-0 flex-1 items-center gap-2">
            <button type="button" disabled={!clickable} onClick={() => onGo(n)}
              className={`flex min-w-0 flex-1 items-center gap-2 rounded-xl px-3 py-2 text-left transition ${active ? 'bg-slate-900 text-white' : clickable ? 'hover:bg-slate-100' : ''} ${!active && !clickable ? 'opacity-50' : ''}`}>
              <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${active ? 'bg-white text-slate-900' : done ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
                {done && !active ? '✓' : n}
              </span>
              <span className={`truncate text-sm font-semibold ${active ? 'text-white' : 'text-slate-700'}`}>{label}</span>
            </button>
            {n < steps.length && <span className="hidden h-px w-6 flex-shrink-0 bg-slate-200 sm:block" />}
          </li>
        );
      })}
    </ol>
  );
}

// Barra fixa no rodapé do passo: voltar, o que já foi escolhido e continuar.
// Fica sempre visível, mesmo com uma lista de produtos longa.
export function WizardFooter({ onBack, backLabel = '← Voltar', info, onNext, nextLabel, nextDisabled, nextHint }: {
  onBack?: () => void; backLabel?: string; info?: ReactNode;
  onNext: () => void; nextLabel: string; nextDisabled?: boolean; nextHint?: string;
}) {
  return (
    <div className="sticky bottom-0 z-20 -mx-1 mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-6px_20px_-12px_rgba(15,23,42,0.35)] backdrop-blur">
      {onBack && <button type="button" onClick={onBack} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">{backLabel}</button>}
      <div className="min-w-0 flex-1">{info}</div>
      <div className="flex flex-col items-end">
        <button type="button" onClick={onNext} disabled={nextDisabled}
          className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-slate-300">
          {nextLabel}
        </button>
        {nextDisabled && nextHint && <span className="mt-1 text-[11px] text-slate-500">{nextHint}</span>}
      </div>
    </div>
  );
}

// Área de upload com clique ou arrastar-e-soltar.
export function DropZone({ onFile, busy, className = '', children }: {
  onFile: (file: File) => void; busy?: boolean; className?: string; children: ReactNode;
}) {
  const [over, setOver] = useState(false);
  return (
    <label
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}
      className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed text-center transition ${over ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 bg-white hover:border-indigo-400'} ${busy ? 'pointer-events-none opacity-60' : ''} ${className}`}
    >
      {children}
      <input type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
    </label>
  );
}

export const JOB_STATUS_STYLE: Record<string, { label: string; cls: string }> = {
  queued: { label: 'Na fila', cls: 'bg-slate-100 text-slate-600' },
  running: { label: 'Gerando', cls: 'bg-indigo-100 text-indigo-700' },
  awaiting_approval: { label: 'Aprovar principal', cls: 'bg-amber-100 text-amber-800' },
  regenerating: { label: 'Regerando', cls: 'bg-indigo-100 text-indigo-700' },
  awaiting_adjustment: { label: 'Escolher ajuste', cls: 'bg-amber-100 text-amber-800' },
  completed: { label: 'Pronto', cls: 'bg-emerald-100 text-emerald-700' },
  failed: { label: 'Falhou', cls: 'bg-rose-100 text-rose-700' },
};
